import { useState } from 'react';
import { supabase } from '@/lib/supabase';

interface UploadFormData {
  formCode: string;
  versionNumber: string;
  effectiveDate: string;
  expirationDate?: string;
  applicableStates: string[];
  changeNotes: string;
  regulatoryRef?: string;
}

interface UploadResult {
  success: boolean;
  message: string;
  versionId?: string;
  fileUrl?: string;
}

export function useFormUpload() {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const uploadFile = async (file: File, formCode: string, versionNumber: string): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${formCode}_v${versionNumber}.${fileExt}`;
      const filePath = `forms/${formCode}/${fileName}`;

      setProgress(25);

      const { data, error } = await supabase.storage
        .from('legal-forms')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true,
        });

      if (error) throw error;

      setProgress(50);

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('legal-forms')
        .getPublicUrl(filePath);

      return urlData.publicUrl;
    } catch (error) {
      console.error('Error uploading file:', error);
      return null;
    }
  };

  const submitFormVersion = async (
    formData: UploadFormData,
    file: File | null
  ): Promise<UploadResult> => {
    setUploading(true);
    setProgress(0);

    try {
      // Validate required fields
      if (!formData.formCode || !formData.versionNumber || !formData.effectiveDate) {
        return {
          success: false,
          message: 'Please fill in all required fields',
        };
      }

      if (!file) {
        return {
          success: false,
          message: 'Please select a file to upload',
        };
      }

      // Validate file type
      const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        return {
          success: false,
          message: 'Only PDF and DOCX files are allowed',
        };
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        return {
          success: false,
          message: 'File size must be less than 10MB',
        };
      }

      setProgress(10);

      // Upload file to storage
      const fileUrl = await uploadFile(file, formData.formCode, formData.versionNumber);

      if (!fileUrl) {
        return {
          success: false,
          message: 'Failed to upload file',
        };
      }

      setProgress(75);

      // Create form version record in database
      const { data: versionData, error: versionError } = await supabase
        .from('form_versions')
        .insert({
          form_code: formData.formCode,
          version_number: formData.versionNumber,
          file_url: fileUrl,
          file_type: file.type.includes('pdf') ? 'pdf' : 'docx',
          effective_date: formData.effectiveDate,
          expiration_date: formData.expirationDate || null,
          applicable_states: formData.applicableStates,
          change_notes: formData.changeNotes,
          regulatory_ref: formData.regulatoryRef || null,
          status: 'pending_approval',
          uploaded_by: (await supabase.auth.getUser()).data.user?.id,
        })
        .select()
        .single();

      if (versionError) throw versionError;

      setProgress(90);

      // Log the upload event
      await supabase.from('form_audit_log').insert({
        form_code: formData.formCode,
        version_id: versionData.id,
        action: 'version_uploaded',
        actor_id: (await supabase.auth.getUser()).data.user?.id,
        details: {
          version_number: formData.versionNumber,
          file_name: file.name,
          file_size: file.size,
        },
      });

      setProgress(100);

      return {
        success: true,
        message: 'Form version uploaded successfully and submitted for approval',
        versionId: versionData.id,
        fileUrl: fileUrl,
      };
    } catch (error) {
      console.error('Error submitting form version:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to upload form version',
      };
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 1000);
    }
  };

  const approveVersion = async (versionId: string, comments?: string): Promise<UploadResult> => {
    try {
      const { error } = await supabase
        .from('form_versions')
        .update({
          status: 'approved',
          approved_by: (await supabase.auth.getUser()).data.user?.id,
          approved_at: new Date().toISOString(),
          approval_comments: comments,
        })
        .eq('id', versionId);

      if (error) throw error;

      // Log approval
      await supabase.from('form_audit_log').insert({
        version_id: versionId,
        action: 'version_approved',
        actor_id: (await supabase.auth.getUser()).data.user?.id,
        details: { comments },
      });

      return {
        success: true,
        message: 'Form version approved successfully',
      };
    } catch (error) {
      console.error('Error approving version:', error);
      return {
        success: false,
        message: 'Failed to approve form version',
      };
    }
  };

  const rejectVersion = async (versionId: string, reason: string): Promise<UploadResult> => {
    try {
      const { error } = await supabase
        .from('form_versions')
        .update({
          status: 'rejected',
          approved_by: (await supabase.auth.getUser()).data.user?.id,
          approved_at: new Date().toISOString(),
          approval_comments: reason,
        })
        .eq('id', versionId);

      if (error) throw error;

      // Log rejection
      await supabase.from('form_audit_log').insert({
        version_id: versionId,
        action: 'version_rejected',
        actor_id: (await supabase.auth.getUser()).data.user?.id,
        details: { reason },
      });

      return {
        success: true,
        message: 'Form version rejected',
      };
    } catch (error) {
      console.error('Error rejecting version:', error);
      return {
        success: false,
        message: 'Failed to reject form version',
      };
    }
  };

  const activateKillSwitch = async (
    versionId: string,
    reason: string,
    complianceIssue?: string
  ): Promise<UploadResult> => {
    try {
      const { error } = await supabase
        .from('form_versions')
        .update({
          status: 'disabled',
          kill_switch_activated: true,
          kill_switch_reason: reason,
          kill_switch_activated_by: (await supabase.auth.getUser()).data.user?.id,
          kill_switch_activated_at: new Date().toISOString(),
        })
        .eq('id', versionId);

      if (error) throw error;

      // Log kill-switch activation
      await supabase.from('form_audit_log').insert({
        version_id: versionId,
        action: 'kill_switch_activated',
        actor_id: (await supabase.auth.getUser()).data.user?.id,
        details: {
          reason,
          compliance_issue: complianceIssue,
        },
      });

      return {
        success: true,
        message: 'Kill-switch activated - form version disabled',
      };
    } catch (error) {
      console.error('Error activating kill-switch:', error);
      return {
        success: false,
        message: 'Failed to activate kill-switch',
      };
    }
  };

  return {
    uploading,
    progress,
    submitFormVersion,
    approveVersion,
    rejectVersion,
    activateKillSwitch,
  };
}
